<template>
  <div>
    <!-- Header -->
    <b-card class="mb-1">
      <template #header>
        <b-row class="flex-row justify-content-between align-items-center" style="width:100%">
          <b-col>
            <h4 class="mb-0">
              Onboarding — {{ account ? account.AccountName : "" }}
              <small v-if="account" class="text-muted ml-50">{{ account.AccountNumber }}</small>
              <b-badge v-if="onboarding" :variant="statusVariant(onboarding.Status)" class="ml-1">
                {{ onboarding.Status }}
              </b-badge>
            </h4>
          </b-col>
          <b-col cols="auto">
            <b-button
              variant="flat-secondary"
              size="sm"
              @click="$router.push({ name: 'onboarding' })"
            >
              <feather-icon icon="ArrowLeftIcon" size="14" style="margin-bottom:0.3rem" />
              <span class="text-nowrap">Back to List</span>
            </b-button>
          </b-col>
        </b-row>
      </template>

      <div v-if="loading" class="text-center my-2">
        <b-spinner class="align-middle mr-1" />
        <strong>Loading…</strong>
      </div>

      <b-tabs v-else content-class="mt-1">
        <!-- ============================== FORM TAB ============================== -->
        <b-tab title="Form" active>
          <b-alert v-if="!formData" show variant="info">
            <feather-icon icon="InfoIcon" class="mr-50" />
            The client has not started filling the form yet.
          </b-alert>

          <template v-else>
            <b-alert v-if="onboarding.Status === 'Signed'" show variant="success" class="p-1">
              <feather-icon icon="CheckCircleIcon" class="mr-50" />
              Signed by <strong>{{ onboarding.SignatureName }}</strong> on
              <strong>{{ onboarding.SignedDate }} {{ onboarding.SignedTime }}</strong>
              from IP <strong>{{ onboarding.SignedIp }}</strong>
            </b-alert>

            <!-- Section A -->
            <b-card title="Section A — Client Information" class="border mb-1">
              <b-row>
                <b-col
                  v-for="row in sectionARows"
                  :key="row.label"
                  cols="12"
                  md="6"
                  class="mb-50"
                >
                  <small class="text-muted d-block">{{ row.label }}</small>
                  <span>{{ row.value || "—" }}</span>
                </b-col>
              </b-row>
              <div v-if="formData.sectionA && (formData.sectionA.authorisedTraders || []).length" class="mt-1">
                <small class="text-muted d-block">Persons / traders authorised to give instructions</small>
                <b-badge v-for="(t, i) in formData.sectionA.authorisedTraders" :key="'at' + i" variant="light-primary" class="mr-25">
                  {{ t.name }}
                </b-badge>
              </div>
              <div v-if="formData.sectionA && (formData.sectionA.nonDealingSignatories || []).length" class="mt-50">
                <small class="text-muted d-block">Signatories authorised to instruct non-dealing matters</small>
                <b-badge v-for="(s, i) in formData.sectionA.nonDealingSignatories" :key="'nd' + i" variant="light-secondary" class="mr-25">
                  {{ s.name }}
                </b-badge>
              </div>
            </b-card>

            <!-- Section B -->
            <b-card title="Section B — Knowledge & Experience" class="border mb-1">
              <b-row>
                <b-col v-for="row in sectionBRows" :key="row.label" cols="12" class="mb-50">
                  <small class="text-muted d-block">{{ row.label }}</small>
                  <span>{{ row.value || "—" }}</span>
                </b-col>
              </b-row>
            </b-card>

            <!-- Sections C & D acknowledgements -->
            <b-card title="Sections C & D — Notices" class="border mb-1">
              <p class="mb-25">
                <feather-icon
                  :icon="ack('sectionC') ? 'CheckSquareIcon' : 'SquareIcon'"
                  :class="ack('sectionC') ? 'text-success' : 'text-muted'"
                  class="mr-50"
                />
                Loss of Protection Notice for Professional Clients — read and understood
              </p>
              <p class="mb-0">
                <feather-icon
                  :icon="ack('sectionD') ? 'CheckSquareIcon' : 'SquareIcon'"
                  :class="ack('sectionD') ? 'text-success' : 'text-muted'"
                  class="mr-50"
                />
                Terms of Business — read and understood
              </p>
            </b-card>

            <!-- Section E -->
            <b-card title="Section E — Client Declaration" class="border mb-1">
              <b-row>
                <b-col cols="12" md="6">
                  <small class="text-muted d-block">Name</small>
                  <span>{{ onboarding.SignatureName || (formData.sectionE && formData.sectionE.name) || "—" }}</span>
                  <small class="text-muted d-block mt-50">Date</small>
                  <span>{{ onboarding.SignedDate || "—" }}</span>
                </b-col>
                <b-col cols="12" md="6">
                  <small class="text-muted d-block">Signature</small>
                  <img
                    v-if="signatureImages.declaration"
                    :src="signatureImages.declaration"
                    alt="Signature"
                    class="border rounded bg-white"
                    style="max-height:120px; max-width:100%"
                  />
                  <span v-else class="text-muted">Not signed</span>
                </b-col>
              </b-row>
            </b-card>

            <!-- Section F -->
            <b-card title="Section F — Re-categorisation of Client Classification" class="border mb-1">
              <b-row>
                <b-col cols="12" md="6">
                  <small class="text-muted d-block">Requested classification</small>
                  <span>{{ classificationLabel }}</span>
                  <template v-if="retailCriteria.length">
                    <small class="text-muted d-block mt-50">Reclassification criteria (Retail → Professional)</small>
                    <span v-for="c in retailCriteria" :key="c" class="d-block">• {{ c }}</span>
                  </template>
                  <small class="text-muted d-block mt-50">Name</small>
                  <span>{{ onboarding.RecatSignatureName || (formData.sectionF && formData.sectionF.name) || "—" }}</span>
                </b-col>
                <b-col cols="12" md="6">
                  <small class="text-muted d-block">Signature</small>
                  <img
                    v-if="signatureImages.recategorisation"
                    :src="signatureImages.recategorisation"
                    alt="Signature"
                    class="border rounded bg-white"
                    style="max-height:120px; max-width:100%"
                  />
                  <span v-else class="text-muted">Not signed</span>
                </b-col>
              </b-row>
            </b-card>

            <!-- Section G -->
            <b-card
              v-if="(formData.sectionG || []).length"
              title="Section G — Appointed Traders"
              class="border mb-1"
            >
              <b-card
                v-for="(trader, index) in formData.sectionG"
                :key="'trader' + index"
                :title="'Appointed Trader ' + (index + 1) + (trader.name ? ' — ' + trader.name : '')"
                class="border mb-1"
              >
                <b-row>
                  <b-col v-for="row in traderRows(trader)" :key="row.label" cols="12" md="6" class="mb-50">
                    <small class="text-muted d-block">{{ row.label }}</small>
                    <span>{{ row.value || "—" }}</span>
                  </b-col>
                  <b-col cols="12" md="6" class="mb-50">
                    <small class="text-muted d-block">Signature</small>
                    <img
                      v-if="signatureImages['trader:' + index]"
                      :src="signatureImages['trader:' + index]"
                      alt="Signature"
                      class="border rounded bg-white"
                      style="max-height:120px; max-width:100%"
                    />
                    <span v-else class="text-muted">Not signed</span>
                  </b-col>
                </b-row>
              </b-card>
            </b-card>

            <!-- Section H -->
            <b-card title="Section H — Additional Information" class="border mb-1">
              <span style="white-space:pre-wrap">{{ (formData.sectionH && formData.sectionH.additionalInfo) || "—" }}</span>
            </b-card>
          </template>
        </b-tab>

        <!-- ============================== REQUIRED DOCUMENTS TAB ============================== -->
        <b-tab title="Required Documents">
          <b-row class="mb-1 align-items-end">
            <b-col cols="12" md="6">
              <b-form-group label="Document type (choose or type a new one)" class="mb-0">
                <v-select
                  v-model="selectedType"
                  :options="documentTypes"
                  label="Name"
                  taggable
                  :create-option="(name) => ({ id: null, Name: name })"
                  placeholder="e.g. Certified copy of passport"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group label="Note (optional)" class="mb-0">
                <b-form-input v-model="requiredDocNote" placeholder="Shown to the client" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="2">
              <b-button variant="primary" block :disabled="!selectedType || requiredDocSaving" @click="addRequiredDocument">
                <b-spinner v-if="requiredDocSaving" small class="mr-25" />
                Add
              </b-button>
            </b-col>
          </b-row>

          <b-table
            :items="requiredDocuments"
            :fields="requiredDocFields"
            show-empty
            empty-text="No documents requested from this client yet."
            responsive
          >
            <template #cell(Status)="{ value }">
              <b-badge :variant="value === 'Uploaded' ? 'success' : 'warning'">{{ value }}</b-badge>
            </template>
            <template #cell(actions)="{ item }">
              <b-button variant="flat-danger" size="sm" @click="removeRequiredDocument(item)">
                <feather-icon icon="Trash2Icon" size="14" />
              </b-button>
            </template>
          </b-table>
        </b-tab>

        <!-- ============================== DOCUMENTS TAB ============================== -->
        <b-tab title="Documents">
          <b-row class="mb-1">
            <b-col>
              <input ref="internalFileInput" type="file" class="d-none" @input="internalFileSelected" />
              <b-button variant="primary" :disabled="uploadingInternal" @click="$refs.internalFileInput.click()">
                <b-spinner v-if="uploadingInternal" small class="mr-50" />
                <feather-icon v-else icon="UploadIcon" size="14" class="mr-50" />
                Upload Document (visible to client)
              </b-button>
            </b-col>
          </b-row>

          <b-table
            :items="documents"
            :fields="documentFields"
            show-empty
            empty-text="No documents uploaded yet."
            responsive
          >
            <template #cell(Source)="{ value }">
              <b-badge :variant="value === 'Client' ? 'light-primary' : 'light-info'">{{ value }}</b-badge>
            </template>
            <template #cell(FileSize)="{ value }">
              {{ formatSize(value) }}
            </template>
            <template #cell(actions)="{ item }">
              <b-button variant="flat-primary" size="sm" @click="downloadDocument(item)">
                <feather-icon icon="DownloadIcon" size="14" />
              </b-button>
              <b-button variant="flat-danger" size="sm" @click="deleteDocument(item)">
                <feather-icon icon="Trash2Icon" size="14" />
              </b-button>
            </template>
          </b-table>
        </b-tab>

        <!-- ============================== CONTACTS TAB ============================== -->
        <b-tab title="Contacts">
          <validation-observer ref="contactValidation">
            <b-row class="mb-50">
              <b-col cols="12" md="6">
                <b-form-group label="Select an existing client user" class="mb-0">
                  <v-select
                    v-model="selectedClientUser"
                    :options="clientUsers"
                    label="fullname"
                    :get-option-label="(u) => u.fullname + ' (' + u.email + ')'"
                    placeholder="Search client users…"
                    :clearable="true"
                  />
                  <small class="text-muted">or enter a name and email manually below</small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row class="mb-1 align-items-end">
              <b-col cols="12" md="3">
                <b-form-group label="Name" class="mb-0">
                  <validation-provider #default="{ errors }" :rules="selectedClientUser ? '' : 'required'" name="Contact Name">
                    <b-form-input
                      v-model="newContact.name"
                      :disabled="!!selectedClientUser"
                      :placeholder="selectedClientUser ? selectedClientUser.fullname : ''"
                      :state="errors.length ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="Email" class="mb-0">
                  <validation-provider #default="{ errors }" :rules="selectedClientUser ? '' : 'required|email'" name="Contact Email">
                    <b-form-input
                      v-model="newContact.email"
                      :disabled="!!selectedClientUser"
                      :placeholder="selectedClientUser ? selectedClientUser.email : ''"
                      :state="errors.length ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-checkbox v-model="sendInviteEmail" class="mb-50">
                  Send invitation email
                </b-form-checkbox>
              </b-col>
              <b-col cols="12" md="3">
                <b-button variant="primary" :disabled="contactSaving" @click="createContact">
                  <b-spinner v-if="contactSaving" small class="mr-25" />
                  <feather-icon v-else :icon="sendInviteEmail ? 'SendIcon' : 'UserPlusIcon'" size="14" class="mr-25" />
                  {{ sendInviteEmail ? "Add Contact & Send Link" : "Add Contact" }}
                </b-button>
              </b-col>
            </b-row>
          </validation-observer>

          <b-table
            :items="contacts"
            :fields="contactFields"
            show-empty
            empty-text="No contacts assigned to this account yet."
            responsive
          >
            <template #cell(Name)="{ item }">
              {{ item.Name }}
              <b-badge v-if="item.UserId" variant="light-info" class="ml-25">System user</b-badge>
            </template>
            <template #cell(Status)="{ value }">
              <b-badge :variant="value === 'Active' ? 'success' : 'danger'">{{ value }}</b-badge>
            </template>
            <template #cell(Link)="{ item }">
              <template v-if="item.Status === 'Active'">
                <code class="d-block text-truncate" style="max-width:280px" :title="contactLink(item)">
                  {{ contactLink(item) }}
                </code>
                <b-link class="small" @click="copyLink(item)">
                  <feather-icon icon="CopyIcon" size="12" class="mr-25" />Copy
                </b-link>
              </template>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(LastAccess)="{ item }">
              <template v-if="item.LastAccessDate">
                {{ item.LastAccessDate }} {{ item.LastAccessTime }}
                <small class="text-muted d-block">IP: {{ item.LastAccessIp }}</small>
              </template>
              <span v-else class="text-muted">Never</span>
            </template>
            <template #cell(actions)="{ item }">
              <b-button
                v-if="item.Status === 'Active'"
                variant="flat-primary"
                size="sm"
                title="Copy link"
                @click="copyLink(item)"
              >
                <feather-icon icon="LinkIcon" size="14" />
              </b-button>
              <b-button
                v-if="item.Status === 'Active'"
                variant="flat-info"
                size="sm"
                title="Resend link email"
                @click="resendLink(item)"
              >
                <feather-icon icon="MailIcon" size="14" />
              </b-button>
              <b-button
                v-if="item.Status === 'Active'"
                variant="flat-danger"
                size="sm"
                title="Revoke access"
                @click="revokeContact(item)"
              >
                <feather-icon icon="SlashIcon" size="14" />
              </b-button>
            </template>
          </b-table>
        </b-tab>

        <!-- ============================== ACTIVITY TAB ============================== -->
        <b-tab title="Activity" @click="loadActivities">
          <app-timeline v-if="activities.length">
            <app-timeline-item
              v-for="activity in activities"
              :key="activity.id"
              :title="eventTitle(activity.Event)"
              :subtitle="activitySubtitle(activity)"
              :time="activity.SaveDate + ' ' + activity.SaveTime"
              :variant="eventVariant(activity.Event)"
              :icon="eventIcon(activity.Event)"
            />
          </app-timeline>
          <b-alert v-else show variant="info">
            <feather-icon icon="InfoIcon" class="mr-50" />
            No activity recorded yet.
          </b-alert>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required, email } from "@validations";
import vSelect from "vue-select";
import AppTimeline from "@core/components/app-timeline/AppTimeline.vue";
import AppTimelineItem from "@core/components/app-timeline/AppTimelineItem.vue";
import store from "@/store";
import onboardingStoreModule from "./onboardingStoreModule";

const STORE_MODULE = "onboarding";

export default {
  name: "OnboardingDetail",

  components: {
    ValidationObserver,
    ValidationProvider,
    vSelect,
    AppTimeline,
    AppTimelineItem,
  },

  data() {
    return {
      loading: true,
      account: null,
      onboarding: null,
      requiredDocuments: [],
      documents: [],
      contacts: [],
      activities: [],
      documentTypes: [],
      signatureImages: {},

      selectedType: null,
      requiredDocNote: "",
      requiredDocSaving: false,

      uploadingInternal: false,

      newContact: { name: "", email: "" },
      selectedClientUser: null,
      clientUsers: [],
      sendInviteEmail: true,
      contactSaving: false,

      requiredDocFields: [
        { key: "TypeName", label: "Document" },
        { key: "Note", label: "Note" },
        { key: "Status", label: "Status" },
        { key: "CreatedBy", label: "Requested By" },
        { key: "actions", label: "" },
      ],
      documentFields: [
        { key: "FileName", label: "File" },
        { key: "Source", label: "Source" },
        { key: "FileSize", label: "Size" },
        { key: "created_at", label: "Uploaded At" },
        { key: "actions", label: "" },
      ],
      contactFields: [
        { key: "Name", label: "Name" },
        { key: "Email", label: "Email" },
        { key: "Status", label: "Status" },
        { key: "Link", label: "Onboarding Link" },
        { key: "LastAccess", label: "Last Access" },
        { key: "actions", label: "" },
      ],
    };
  },

  computed: {
    formData() {
      if (!this.onboarding || !this.onboarding.FormData) return null;
      try {
        return typeof this.onboarding.FormData === "string"
          ? JSON.parse(this.onboarding.FormData)
          : this.onboarding.FormData;
      } catch (e) {
        return null;
      }
    },

    sectionARows() {
      const a = (this.formData && this.formData.sectionA) || {};
      return [
        { label: "Account Name", value: a.accountName },
        { label: "Client Type", value: a.clientType },
        { label: "Address (registered office)", value: a.address },
        { label: "Name & address on contracts / statements", value: a.contractsNameAddress },
        { label: "Frequency of daily reports", value: a.reportFrequency },
        { label: "Form of reports", value: a.reportForm === "other" ? a.reportFormOther : a.reportForm },
        { label: "Company registration number", value: a.companyRegNumber },
        { label: "Country tax domiciled", value: a.taxCountry },
        { label: "Nature of business", value: a.natureOfBusiness },
        { label: "Name & address of bankers", value: a.bankersNameAddress },
        { label: "Tel / Home", value: a.telHome },
        { label: "Tel / Office", value: a.telOffice },
        { label: "Mobile", value: a.mobile },
        { label: "Fax", value: a.fax },
        { label: "Email", value: a.email },
        { label: "Sort code / SWIFT address", value: a.sortCodeSwift },
        { label: "Bank account name", value: a.bankAccountName },
        { label: "Bank account number", value: a.bankAccountNumber },
        { label: "Name of officer", value: a.officerName },
      ];
    },

    sectionBRows() {
      const b = (this.formData && this.formData.sectionB) || {};
      return [
        { label: "1. Years of experience in the markets you wish to trade", value: b.yearsExperience },
        { label: "2. Overall frequency of previous dealings", value: b.dealingFrequency },
        { label: "3. Typical size of transactions", value: b.typicalSize },
        { label: "4. Capacity active in these markets", value: b.capacity },
        { label: "5. Trading basis (long term / speculative / hedging)", value: b.tradingBasis },
        { label: "6. Other financial market experience", value: b.otherExperience },
        { label: "7. Understands the risks and potential for loss", value: b.understandsRisks },
      ];
    },

    classificationLabel() {
      const f = (this.formData && this.formData.sectionF) || {};
      const labels = {
        Professional: "Professional Client (per se or elective)",
        Retail: "Retail Client (re-classification request)",
        EligibleCounterparty: "Eligible Counterparty (re-classification request)",
      };
      return labels[f.classification] || "—";
    },

    retailCriteria() {
      const f = (this.formData && this.formData.sectionF) || {};
      const c = f.criteria || {};
      const out = [];
      if (c.transactions) out.push("10+ significant transactions per quarter over the last four quarters");
      if (c.portfolio) out.push("Financial portfolio exceeding EUR 500,000");
      if (c.professional) out.push("Worked in the financial sector for at least one year in a professional position");
      return out;
    },
  },

  beforeCreate() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, onboardingStoreModule);
    }
  },

  mounted() {
    this.loadAll();
  },

  destroyed() {
    if (store.hasModule(STORE_MODULE)) {
      store.unregisterModule(STORE_MODULE);
    }
    Object.values(this.signatureImages).forEach((url) => URL.revokeObjectURL(url));
  },

  methods: {
    loadAll() {
      this.loading = true;
      Promise.all([
        store.dispatch(`${STORE_MODULE}/getAccountOnboarding`, { accountId: this.$route.params.id }),
        store.dispatch(`${STORE_MODULE}/getDocumentTypes`),
        store.dispatch(`${STORE_MODULE}/getClientUsers`),
      ])
        .then(([onboardingRes, typesRes, clientUsersRes]) => {
          this.applyPayload(onboardingRes.data);
          this.documentTypes = typesRes.data || [];
          this.clientUsers = clientUsersRes.data || [];
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Failed to load onboarding data.", { title: "Error", variant: "danger", solid: true });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    applyPayload(payload) {
      this.account = payload.account;
      this.onboarding = payload.onboarding;
      this.requiredDocuments = payload.requiredDocuments || [];
      this.documents = payload.documents || [];
      this.contacts = payload.contacts || [];
      this.activities = payload.activities || [];
      this.loadSignatures();
    },

    refresh() {
      return store
        .dispatch(`${STORE_MODULE}/getAccountOnboarding`, { accountId: this.$route.params.id })
        .then((res) => this.applyPayload(res.data));
    },

    loadSignatures() {
      if (!this.onboarding || this.onboarding.Status !== "Signed") return;

      const wanted = [];
      if (this.onboarding.SignatureFile) wanted.push("declaration");
      if (this.onboarding.RecatSignatureFile) wanted.push("recategorisation");
      ((this.formData && this.formData.sectionG) || []).forEach((trader, index) => {
        if (trader.signatureFile) wanted.push("trader:" + index);
      });

      wanted.forEach((which) => {
        if (this.signatureImages[which]) return;
        store
          .dispatch(`${STORE_MODULE}/getSignatureImage`, { onboardingId: this.onboarding.id, which })
          .then((res) => {
            this.$set(this.signatureImages, which, URL.createObjectURL(res.data));
          })
          .catch(() => {});
      });
    },

    ack(section) {
      return !!(this.formData && this.formData[section] && this.formData[section].acknowledged);
    },

    traderRows(trader) {
      return [
        { label: "Name", value: trader.name },
        { label: "Email", value: trader.email },
        { label: "Mobile", value: trader.mobile },
        { label: "Tel / Home", value: trader.telHome },
        { label: "Tel / Office", value: trader.telOffice },
        { label: "Fax", value: trader.fax },
        { label: "1. Years of experience", value: trader.yearsExperience },
        { label: "2. Frequency of dealings", value: trader.dealingFrequency },
        { label: "3. Typical transaction size", value: trader.typicalSize },
        { label: "4. Capacity", value: trader.capacity },
        { label: "5. Trading basis", value: trader.tradingBasis },
        { label: "6. Other experience", value: trader.otherExperience },
        { label: "7. Understands the risks", value: trader.understandsRisks },
      ];
    },

    /* ---------- Required documents ---------- */
    addRequiredDocument() {
      if (!this.selectedType) return;
      this.requiredDocSaving = true;

      const ensureType =
        this.selectedType.id
          ? Promise.resolve(this.selectedType)
          : store
              .dispatch(`${STORE_MODULE}/createDocumentType`, { name: this.selectedType.Name })
              .then((res) => res.data);

      ensureType
        .then((type) =>
          store.dispatch(`${STORE_MODULE}/addRequiredDocument`, {
            onboardingId: this.onboarding.id,
            documentTypeId: type.id,
            note: this.requiredDocNote || null,
          })
        )
        .then(() => {
          this.selectedType = null;
          this.requiredDocNote = "";
          return Promise.all([
            this.refresh(),
            store.dispatch(`${STORE_MODULE}/getDocumentTypes`).then((res) => {
              this.documentTypes = res.data || [];
            }),
          ]);
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Failed to add required document.", { title: "Error", variant: "danger", solid: true });
        })
        .finally(() => {
          this.requiredDocSaving = false;
        });
    },

    removeRequiredDocument(item) {
      store
        .dispatch(`${STORE_MODULE}/removeRequiredDocument`, { requiredDocumentId: item.id })
        .then(() => this.refresh())
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Failed to remove required document.", { title: "Error", variant: "danger", solid: true });
        });
    },

    /* ---------- Documents ---------- */
    internalFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploadingInternal = true;
      const formData = new FormData();
      formData.append("onboardingId", this.onboarding.id);
      formData.append("file", file);

      store
        .dispatch(`${STORE_MODULE}/uploadInternalDocument`, formData)
        .then(() => {
          this.$bvToast.toast("Document uploaded.", { title: "Success", variant: "success", solid: true });
          return this.refresh();
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Upload failed.", { title: "Error", variant: "danger", solid: true });
        })
        .finally(() => {
          this.uploadingInternal = false;
          this.$refs.internalFileInput.value = "";
        });
    },

    downloadDocument(item) {
      store
        .dispatch(`${STORE_MODULE}/downloadDocument`, { documentId: item.id })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          const link = document.createElement("a");
          link.href = url;
          link.download = item.FileName;
          link.click();
          URL.revokeObjectURL(url);
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Download failed.", { title: "Error", variant: "danger", solid: true });
        });
    },

    deleteDocument(item) {
      this.$bvModal
        .msgBoxConfirm(`Delete "${item.FileName}"? The client will no longer see it.`, {
          title: "Delete Document",
          okVariant: "danger",
          okTitle: "Delete",
          centered: true,
        })
        .then((confirmed) => {
          if (!confirmed) return;
          store
            .dispatch(`${STORE_MODULE}/deleteDocument`, { documentId: item.id })
            .then(() => this.refresh())
            .catch((err) => {
              console.error(err);
              this.$bvToast.toast("Delete failed.", { title: "Error", variant: "danger", solid: true });
            });
        });
    },

    /* ---------- Contacts ---------- */
    createContact() {
      this.$refs.contactValidation.validate().then((valid) => {
        if (!valid) return;
        this.contactSaving = true;
        store
          .dispatch(`${STORE_MODULE}/createContact`, {
            onboardingId: this.onboarding.id,
            userId: this.selectedClientUser ? this.selectedClientUser.id : null,
            name: this.selectedClientUser ? null : this.newContact.name,
            email: this.selectedClientUser ? null : this.newContact.email,
            sendEmail: this.sendInviteEmail,
          })
          .then((res) => {
            this.$bvToast.toast(
              (res.data && res.data.message) ||
                (this.sendInviteEmail ? "Contact added and onboarding link emailed." : "Contact added."),
              { title: "Success", variant: "success", solid: true }
            );
            this.newContact = { name: "", email: "" };
            this.selectedClientUser = null;
            this.$refs.contactValidation.reset();
            return this.refresh();
          })
          .catch((err) => {
            console.error(err);
            this.$bvToast.toast(
              (err.response && err.response.data && err.response.data.message) || "Failed to add contact.",
              { title: "Error", variant: "danger", solid: true }
            );
          })
          .finally(() => {
            this.contactSaving = false;
          });
      });
    },

    contactLink(item) {
      return `${window.location.origin}/client-onboarding/${item.Token}`;
    },

    copyLink(item) {
      const link = this.contactLink(item);
      navigator.clipboard.writeText(link).then(() => {
        this.$bvToast.toast("Link copied to clipboard.", { title: "Copied", variant: "success", solid: true });
      });
    },

    resendLink(item) {
      store
        .dispatch(`${STORE_MODULE}/resendContactLink`, { contactId: item.id })
        .then(() => {
          this.$bvToast.toast(`Link re-sent to ${item.Email}.`, { title: "Success", variant: "success", solid: true });
          return this.refresh();
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast("Failed to send link.", { title: "Error", variant: "danger", solid: true });
        });
    },

    revokeContact(item) {
      this.$bvModal
        .msgBoxConfirm(`Revoke access for ${item.Name}? Their link will stop working.`, {
          title: "Revoke Access",
          okVariant: "danger",
          okTitle: "Revoke",
          centered: true,
        })
        .then((confirmed) => {
          if (!confirmed) return;
          store
            .dispatch(`${STORE_MODULE}/revokeContact`, { contactId: item.id })
            .then(() => this.refresh())
            .catch((err) => {
              console.error(err);
              this.$bvToast.toast("Failed to revoke contact.", { title: "Error", variant: "danger", solid: true });
            });
        });
    },

    /* ---------- Activity ---------- */
    loadActivities() {
      store
        .dispatch(`${STORE_MODULE}/getActivityLog`, { onboardingId: this.onboarding.id })
        .then((res) => {
          this.activities = res.data || [];
        })
        .catch((err) => console.error(err));
    },

    activitySubtitle(activity) {
      let subtitle = activity.Description || "";
      if (activity.Ip) subtitle += ` — IP: ${activity.Ip}`;
      return subtitle;
    },

    eventTitle(event) {
      const titles = {
        LinkSent: "Link Sent",
        Accessed: "Page Accessed",
        FormSaved: "Form Saved",
        FormSigned: "Form Signed",
        DocumentUploaded: "Document Uploaded",
        DocumentDeleted: "Document Deleted",
        ContactAdded: "Contact Added",
        ContactRevoked: "Contact Revoked",
        RequiredDocAdded: "Required Document Added",
        RequiredDocRemoved: "Required Document Removed",
      };
      return titles[event] || event;
    },

    eventVariant(event) {
      const variants = {
        FormSigned: "success",
        Accessed: "info",
        DocumentUploaded: "primary",
        DocumentDeleted: "danger",
        ContactRevoked: "danger",
        LinkSent: "warning",
      };
      return variants[event] || "secondary";
    },

    eventIcon(event) {
      const icons = {
        LinkSent: "MailIcon",
        Accessed: "EyeIcon",
        FormSaved: "SaveIcon",
        FormSigned: "PenToolIcon",
        DocumentUploaded: "UploadIcon",
        DocumentDeleted: "Trash2Icon",
        ContactAdded: "UserPlusIcon",
        ContactRevoked: "UserXIcon",
        RequiredDocAdded: "FilePlusIcon",
        RequiredDocRemoved: "FileMinusIcon",
      };
      return icons[event] || "ActivityIcon";
    },

    formatSize(bytes) {
      if (!bytes) return "—";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },

    statusVariant(status) {
      switch (status) {
        case "Signed":
          return "success";
        case "InProgress":
          return "warning";
        case "Draft":
          return "info";
        default:
          return "secondary";
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
